import { z } from 'zod';
import { isEvaBrand } from './brand';

export const CATEGORIAS_DE_TUTORIAL = ['Comece por aqui', 'Equipe Interna', 'Pacientes', 'Jornadas', 'Configurações'] as const;
/** IDs estáveis; categorias organizam o acervo, funcionalidades acionam o tutorial. */
export const FUNCIONALIDADES_DO_TUTORIAL = [
  { id: 'equipe', label: 'Equipe Interna', path: '/team' },
  { id: 'profissionais', label: 'Cadastro de profissionais', path: '/providers' },
  { id: 'pacientes', label: 'Pacientes', path: '/families' },
  { id: 'empresas', label: 'Empresas', path: '/companies' },
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { id: 'chat', label: 'Chat', path: '/chat' },
  { id: 'comunicados', label: 'Conteúdo · Comunicados', path: '/content/announcements' },
  { id: 'revistas', label: 'Conteúdo · Revistas', path: '/content/magazines' },
  { id: 'videos', label: 'Conteúdo · Vídeos', path: '/content/videos' },
  { id: 'trilhas', label: 'Conteúdo · Trilhas', path: '/content/trails' },
  { id: 'eventos', label: 'Conteúdo · Eventos', path: '/content/eventos' },
  { id: 'jornadas', label: 'Jornadas · Construtor', path: '/care-coordination/builder' },
  { id: 'formularios', label: 'Jornadas · Formulários', path: '/care-coordination/forms' },
  { id: 'configuracoes', label: 'Configurações (geral)', path: '/settings' },
  { id: 'bloqueios', label: 'Configurações · Controle de CPF', path: '/settings/bloqueios' },
  { id: 'onboarding', label: 'Configurações · Onboarding da marca', path: '/settings/onboarding' },
] as const;
export type FuncionalidadeDoTutorial = typeof FUNCIONALIDADES_DO_TUTORIAL[number]['id'];
export const FuncionalidadeDoTutorialSchema = z.string().refine(
  (id): id is FuncionalidadeDoTutorial => FUNCIONALIDADES_DO_TUTORIAL.some((f) => f.id === id),
  'Funcionalidade inválida.',
);
export function tutorialCorrespondeAoCaminho(id: string | null, caminho: string): boolean {
  const f = FUNCIONALIDADES_DO_TUTORIAL.find((item) => item.id === id);
  return !!f && (caminho === f.path || caminho.startsWith(`${f.path}/`));
}

type UsuarioDoTutorial = {
  role?: string; brandRole?: string; brandId?: string | null; partnerId?: string | null;
  permissionsPresets?: string | string[];
};
/** O papel do contexto prevalece sobre presets legados de outros vínculos. */
export function acessoAosPrimeirosPassos(user: UsuarioDoTutorial | null | undefined) {
  if (!user) return { ler: false, editar: false };
  const presets = Array.isArray(user.permissionsPresets) ? user.permissionsPresets : [user.permissionsPresets].filter(Boolean);
  if (user.brandRole) {
    const ler = user.brandRole === 'BRAND_ADMIN' || user.brandRole === 'MAINTAINER';
    const marca = user.brandId ?? user.partnerId;
    const editar = user.brandRole === 'MAINTAINER' || (user.brandRole === 'BRAND_ADMIN' && !!marca && isEvaBrand(marca));
    return { ler, editar };
  }
  if (user.role !== 'INTERNAL') return { ler: false, editar: false };
  const parceiro = presets.some((p) => p?.startsWith('PARTNER_')) || presets.includes('COMPANY_MANAGER');
  const ler = presets.includes('PARTNER_ADMIN') || !parceiro;
  // Conta PARTNER_ADMIN sem marca nunca vira equipe Eva por normalização.
  const marca = user.brandId ?? user.partnerId;
  return { ler, editar: ler && (marca ? isEvaBrand(marca) : !parceiro) };
}

export const TutorialEdicaoSchema = z.object({
  title: z.string().trim().min(1).max(160),
  category: z.enum(CATEGORIAS_DE_TUTORIAL),
  feature: FuncionalidadeDoTutorialSchema.nullable().default(null),
  published: z.boolean().default(false),
});
export const TutorialMediaSchema = z.object({
  filename: z.string(), videoId: z.string(),
  status: z.enum(['TRANSCODING', 'READY', 'FAILED']),
  jobId: z.string().optional(), error: z.string().optional(),
  durationSeconds: z.number().finite().positive(),
  resolutions: z.record(z.string()).default({}),
});
export const TutorialSchema = TutorialEdicaoSchema.extend({
  id: z.string(), order: z.number().int().default(0),
  createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
  media: TutorialMediaSchema.nullable().default(null),
  thumbnail: z.string().nullable().default(null),
  // URLs efêmeras, preenchidas só na resposta.
  thumbnailUrl: z.string().optional(),
});
export type Tutorial = z.infer<typeof TutorialSchema>;
export type TutorialEdicao = z.input<typeof TutorialEdicaoSchema>;

export const AvisoEdicaoSchema = z.object({
  title: z.string().trim().min(1).max(160),
  text: z.string().trim().min(1).max(1500),
  type: z.enum(['novidade', 'manutenção', 'aviso']),
  date: z.string().datetime(),
  published: z.boolean().default(false),
});
export const AvisoDosPrimeirosPassosSchema = AvisoEdicaoSchema.extend({
  id: z.string(), order: z.number().int().default(0),
});
export type AvisoDosPrimeirosPassos = z.infer<typeof AvisoDosPrimeirosPassosSchema>;
const link = z.union([z.literal(''), z.string().url().max(2000).refine((v) => new URL(v).protocol === 'https:', 'Use um link HTTPS.')]);
export const AjudaDosPrimeirosPassosSchema = z.object({
  whatsappUrl: link.default('https://wa.me/5511926937033'),
  bugUrl: link.default(''),
});
export type AjudaDosPrimeirosPassos = z.infer<typeof AjudaDosPrimeirosPassosSchema>;
export interface PrimeirosPassos {
  tutorials: Tutorial[];
  announcements: AvisoDosPrimeirosPassos[];
  help: AjudaDosPrimeirosPassos;
  /** Tutorial → versão do vídeo confirmada. Editar título não apaga progresso. */
  watched: Record<string, string>;
  canManage: boolean;
}
export function tutorialAssistido(t: Tutorial, watched: Record<string, string>): boolean {
  return !!t.media && watched[t.id] === t.media.videoId;
}
